/**
 * InlineDataTable - Styled table rendered inline in chat messages
 *
 * Features:
 * - Sort by any column (click header)
 * - Filter: text search for text/number, date range (De/Até) for dates
 * - Theme-aware styling (navy header, gold hover, brand warnings)
 * - Sticky header with horizontal/vertical scroll
 * - Action bar: Export Excel + Send by Email
 * - Dark mode support
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import type { TableData, TableColumn } from '../../types/tableData'
import { downloadTableAsXlsx } from '../../utils/exportXlsx'
import { EmailReportModal } from './EmailReportModal'
import { WhatsAppMessageModal } from './WhatsAppMessageModal'
import { WhatsAppTextModal } from './WhatsAppTextModal'

// Tabelas visuais (cor por linha) compartilham como PNG; demais compartilham
// como texto formatado (saudação + quote do histórico). Discriminador via
// `email_template` setado pelo backend por tool.
function shouldUseImageWhatsApp(data: TableData): boolean {
  if (data.email_template === 'pedidos_csps') return true
  if (data.row_color_field) return true
  return false
}

interface InlineDataTableProps {
  data: TableData
}

// Detalhe do HD — fullscreen no mobile, modal no desktop
const RowDetailModal: React.FC<{
  row: Record<string, unknown>
  detail: Record<string, unknown>
  onClose: () => void
}> = ({ row, detail, onClose }) => {
  const historico = (detail.historico ?? []) as Array<{
    registrado_por: string
    data: string
    observacao: string
  }>

  const fields = [
    { label: 'Placa', value: detail.placa },
    { label: 'Empresa Proprietária', value: detail.empresa_proprietaria },
    { label: 'Técnico Responsável', value: detail.tecnico_responsavel },
    { label: 'Km', value: detail.km },
    { label: 'Falha', value: detail.falha },
    { label: 'Peça Falha', value: detail.peca_falha },
    { label: 'Email Dealer', value: detail.email_dealer },
    { label: 'Data Liberação', value: detail.data_liberacao_veiculo },
  ].filter(f => f.value)

  const content = (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-surface-elevated border-l-4 border-l-navy-800 dark:border-l-navy-500 shrink-0">
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-content-secondary transition-colors shrink-0 lg:hidden"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-content-primary truncate">
            HD {String(row.numero ?? '')}
          </h3>
          <p className="text-xs text-content-secondary truncate">
            OS {String(row.numero_os ?? '')} — Chassi {String(row.chassi ?? '')}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-content-secondary transition-colors shrink-0 hidden lg:block"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Conteudo scrollavel */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {/* Campos */}
        {fields.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
            {fields.map(f => (
              <div
                key={f.label}
                className="bg-white dark:bg-gray-900 px-3 py-2.5"
              >
                <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {f.label}
                </span>
                <p className="text-xs font-medium text-gray-900 dark:text-gray-100 mt-0.5 break-words">
                  {String(f.value)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Historico */}
        {historico.length > 0 && (
          <div className="px-4 py-3">
            <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider mb-2">
              Histórico ({historico.length})
            </h4>
            <div className="space-y-0 divide-y divide-gray-100 dark:divide-gray-800">
              {historico.map((h, i) => (
                <div key={i} className="py-2.5">
                  <div className="flex items-baseline justify-between gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                      {h.registrado_por}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap shrink-0">
                      {h.data}
                    </span>
                  </div>
                  <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
                    {h.observacao}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {fields.length === 0 && historico.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-8">
            Nenhum detalhe adicional disponível.
          </p>
        )}
      </div>
    </>
  )

  return (
    <>
      {/* Mobile: fullscreen */}
      <div className="fixed inset-0 z-[9999] bg-white dark:bg-gray-900 flex flex-col lg:hidden">
        {content}
      </div>
      {/* Desktop: modal com overlay */}
      <div
        className="fixed inset-0 z-[9999] hidden lg:flex items-center justify-center"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div
          className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-scale-in"
          onClick={e => e.stopPropagation()}
        >
          {content}
        </div>
      </div>
    </>
  )
}

type SortDir = 'asc' | 'desc' | null

interface DateRange {
  from?: string // YYYY-MM-DD (native input format)
  to?: string
}

function formatCellValue(value: unknown, format?: string): string {
  if (value === null || value === undefined || value === '') return '\u2014'

  if (format === 'currency_brl' && typeof value === 'number') {
    return value
      .toFixed(2)
      .replace('.', ',')
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }

  return String(value)
}

/**
 * Mapa sem\u00e2ntico \u2192 design system para a coluna `detail_status`.
 *
 * Backend emite valores sem\u00e2nticos ("ok" | "failed"); o front \u00e9 respons\u00e1vel por
 * traduzir para o \u00edcone do design system (ver coment\u00e1rio em
 * apps/agents/tools/list_open_helpdesks.py no repo do backend).
 *
 * - `ok` \u2192 success (check-circle verde): HD com detalhes completos.
 * - `failed` \u2192 warning (alert-triangle \u00e2mbar): detalhe n\u00e3o p\u00f4de ser extra\u00eddo;
 *   dado parcial, usu\u00e1rio pode re-tentar. N\u00e3o \u00e9 erro terminal, da\u00ed warning.
 */
const DETAIL_STATUS_ICON: Record<
  string,
  { name: 'check-circle' | 'alert-triangle'; color: string; label: string }
> = {
  ok: {
    name: 'check-circle',
    color: 'text-green-500',
    label: 'Detalhes completos',
  },
  failed: {
    name: 'alert-triangle',
    color: 'text-amber-500',
    label: 'Detalhes incompletos',
  },
}

function compareValues(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return 1
  if (b === null || b === undefined) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' })
}

/** Convert DD/MM/YYYY to YYYY-MM-DD for comparison */
function parseDateBr(val: unknown): string | null {
  if (typeof val !== 'string') return null
  const m = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  return `${m[3]}-${m[2]}-${m[1]}`
}

const DateMaskInput: React.FC<{
  value: string
  onChange: (iso: string) => void
  placeholder: string
}> = ({ value, onChange, placeholder }) => {
  const toDisplay = (iso: string) => {
    if (!iso) return ''
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
  }

  const [display, setDisplay] = useState(toDisplay(value))

  React.useEffect(() => {
    setDisplay(toDisplay(value))
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '')
    let masked = ''
    for (let i = 0; i < raw.length && i < 8; i++) {
      if (i === 2 || i === 4) masked += '/'
      masked += raw[i]
    }
    setDisplay(masked)

    if (raw.length === 8) {
      onChange(`${raw.slice(4, 8)}-${raw.slice(2, 4)}-${raw.slice(0, 2)}`)
    } else if (raw.length === 0) {
      onChange('')
    }
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      value={display}
      onChange={handleChange}
      placeholder={placeholder}
      maxLength={10}
      className="w-full px-1.5 py-0.5 text-[10px] font-normal bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
    />
  )
}

export const InlineDataTable: React.FC<InlineDataTableProps> = ({ data }) => {
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [selectedRow, setSelectedRow] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>(null)
  const [textFilters, setTextFilters] = useState<Record<string, string>>({})
  const [dateFilters, setDateFilters] = useState<Record<string, DateRange>>({})
  const [showFilters, setShowFilters] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [colWidths, setColWidths] = useState<Record<string, number>>({})
  const resizingCol = useRef<{
    key: string
    startX: number
    startW: number
  } | null>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  // Column resize handlers
  const onResizeStart = useCallback((e: React.MouseEvent, colKey: string) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.target as HTMLElement).closest('th')
    const startW = th?.offsetWidth || 120
    resizingCol.current = { key: colKey, startX: e.clientX, startW }

    const onMouseMove = (ev: MouseEvent) => {
      if (!resizingCol.current) return
      const diff = ev.clientX - resizingCol.current.startX
      const newW = Math.max(50, resizingCol.current.startW + diff)
      setColWidths(prev => ({ ...prev, [resizingCol.current!.key]: newW }))
    }
    const onMouseUp = () => {
      resizingCol.current = null
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }, [])

  const supportsFullscreen =
    typeof document.documentElement?.requestFullscreen === 'function'

  const toggleFullscreen = useCallback(async () => {
    if (!tableContainerRef.current) return

    if (supportsFullscreen) {
      if (!document.fullscreenElement) {
        try {
          await tableContainerRef.current.requestFullscreen()
          try {
            await (screen.orientation as any)?.lock?.('landscape')
          } catch {
            // Orientation lock not supported
          }
        } catch {
          // Fullscreen failed — use overlay fallback
          setIsFullscreen(true)
        }
      } else {
        try {
          await document.exitFullscreen()
        } catch {
          setIsFullscreen(false)
        }
      }
    } else {
      // Fallback for browsers without Fullscreen API (iOS Safari)
      setIsFullscreen(prev => !prev)
    }
  }, [supportsFullscreen])

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const dateColumns = useMemo(() => {
    const fromFormat = data.columns
      .filter(c => c.format === 'date_br')
      .map(c => c.key)

    // Auto-detect date columns by checking first non-empty value
    const fromContent = data.columns
      .filter(c => !c.format && data.rows.length > 0)
      .filter(c => {
        const sample = data.rows.find(r => r[c.key] != null)?.[c.key]
        return (
          typeof sample === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(sample)
        )
      })
      .map(c => c.key)

    return new Set([...fromFormat, ...fromContent])
  }, [data.columns, data.rows])

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        if (sortDir === 'asc') setSortDir('desc')
        else if (sortDir === 'desc') {
          setSortKey(null)
          setSortDir(null)
        }
      } else {
        setSortKey(key)
        setSortDir('asc')
      }
    },
    [sortKey, sortDir]
  )

  const handleTextFilter = useCallback((key: string, value: string) => {
    setTextFilters(prev => {
      if (!value) {
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: value }
    })
  }, [])

  const handleDateFilter = useCallback(
    (key: string, field: 'from' | 'to', value: string) => {
      setDateFilters(prev => {
        const current = prev[key] || {}
        const updated = { ...current, [field]: value || undefined }
        if (!updated.from && !updated.to) {
          const next = { ...prev }
          delete next[key]
          return next
        }
        return { ...prev, [key]: updated }
      })
    },
    []
  )

  const clearFilters = useCallback(() => {
    setTextFilters({})
    setDateFilters({})
  }, [])

  const activeFilterCount =
    Object.keys(textFilters).length + Object.keys(dateFilters).length

  const processedRows = useMemo(() => {
    let rows = data.rows

    // Text filters
    const activeTextFilters = Object.entries(textFilters)
    if (activeTextFilters.length > 0) {
      rows = rows.filter(row =>
        activeTextFilters.every(([key, term]) => {
          const val = row[key]
          if (val === null || val === undefined) return false
          return String(val).toLowerCase().includes(term.toLowerCase())
        })
      )
    }

    // Date range filters
    const activeDateFilters = Object.entries(dateFilters)
    if (activeDateFilters.length > 0) {
      rows = rows.filter(row =>
        activeDateFilters.every(([key, range]) => {
          const iso = parseDateBr(row[key])
          if (!iso) return false
          if (range.from && iso < range.from) return false
          if (range.to && iso > range.to) return false
          return true
        })
      )
    }

    // Sort
    if (sortKey && sortDir) {
      const isDate = dateColumns.has(sortKey)
      rows = [...rows].sort((a, b) => {
        let va: unknown = a[sortKey]
        let vb: unknown = b[sortKey]
        if (isDate) {
          va = parseDateBr(va) ?? ''
          vb = parseDateBr(vb) ?? ''
        }
        const cmp = compareValues(va, vb)
        return sortDir === 'desc' ? -cmp : cmp
      })
    }

    return rows
  }, [data.rows, textFilters, dateFilters, sortKey, sortDir, dateColumns])

  const hasAnyDetail = useMemo(
    () => processedRows.some(r => !!r.detail),
    [processedRows]
  )

  const handleExportXlsx = useCallback(async () => {
    setExporting(true)
    try {
      await downloadTableAsXlsx({ ...data, rows: processedRows })
    } finally {
      setExporting(false)
    }
  }, [data, processedRows])

  const renderSortIcon = (colKey: string) => {
    if (sortKey !== colKey)
      return (
        <svg
          className="w-3 h-3 ml-1 inline-block opacity-30"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      )
    return (
      <svg
        className="w-3 h-3 ml-1 inline-block text-brand-500"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
        />
      </svg>
    )
  }

  return (
    <>
      {/* Table container */}
      <div
        ref={tableContainerRef}
        className={`border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden ${isFullscreen ? 'flex flex-col bg-white dark:bg-gray-900' : ''} ${isFullscreen && !document.fullscreenElement ? 'fixed inset-0 z-[9999] rounded-none' : ''}`}
      >
        {/* Header bar */}
        <div className="flex items-center justify-between px-3 py-2 bg-surface-elevated border-b border-gray-200 dark:border-gray-700 border-l-4 border-l-navy-800 dark:border-l-navy-500">
          <div>
            <h4 className="text-sm font-semibold text-content-primary">
              {data.title}
            </h4>
            {data.subtitle && (
              <p className="text-xs text-content-secondary">{data.subtitle}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(prev => !prev)}
              className={`p-1 rounded transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300' : 'text-content-tertiary hover:text-content-primary'}`}
              title={showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
            </button>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-[10px] text-gray-300 hover:text-white underline"
              >
                Limpar ({activeFilterCount})
              </button>
            )}
            {data.summary &&
              data.summary.total_value != null &&
              data.summary.total_value > 0 && (
                <span className="text-xs text-content-secondary hidden sm:block">
                  R${' '}
                  {data.summary.total_value
                    .toFixed(2)
                    .replace('.', ',')
                    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                </span>
              )}
            <button
              onClick={toggleFullscreen}
              className="p-1 rounded transition-colors text-content-tertiary hover:text-content-primary"
              title={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
            >
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isFullscreen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                  />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable table */}
        <div
          className={`overflow-auto ${isFullscreen ? 'flex-1' : 'max-h-[60vh]'}`}
        >
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-100 dark:bg-gray-800">
                {hasAnyDetail && <th className="w-8 px-1 py-2" />}
                {data.columns.map((col: TableColumn) => (
                  <th
                    key={col.key}
                    style={
                      colWidths[col.key]
                        ? {
                            width: colWidths[col.key],
                            minWidth: colWidths[col.key],
                          }
                        : undefined
                    }
                    className={`relative px-3 py-2 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors select-none ${
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                          ? 'text-center'
                          : 'text-left'
                    }`}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    {renderSortIcon(col.key)}
                    {/* Resize handle */}
                    <span
                      onMouseDown={e => onResizeStart(e, col.key)}
                      className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand-400 active:bg-brand-500"
                      onClick={e => e.stopPropagation()}
                    />
                  </th>
                ))}
              </tr>
              {showFilters && (
                <tr className="bg-gray-50 dark:bg-gray-800/80">
                  {hasAnyDetail && <th className="w-8" />}
                  {data.columns.map((col: TableColumn) =>
                    dateColumns.has(col.key) ? (
                      <th key={`filter-${col.key}`} className="px-1 py-1">
                        <div className="flex flex-col gap-0.5">
                          <DateMaskInput
                            value={(dateFilters[col.key] || {}).from || ''}
                            onChange={v => handleDateFilter(col.key, 'from', v)}
                            placeholder="De: DD/MM/AAAA"
                          />
                          <DateMaskInput
                            value={(dateFilters[col.key] || {}).to || ''}
                            onChange={v => handleDateFilter(col.key, 'to', v)}
                            placeholder="Até: DD/MM/AAAA"
                          />
                        </div>
                      </th>
                    ) : (
                      <th key={`filter-${col.key}`} className="px-1.5 py-1.5">
                        <input
                          type="text"
                          value={textFilters[col.key] || ''}
                          onChange={e =>
                            handleTextFilter(col.key, e.target.value)
                          }
                          placeholder="Filtrar..."
                          className="w-full px-2 py-1 text-[10px] font-normal bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        />
                      </th>
                    )
                  )}
                </tr>
              )}
            </thead>
            <tbody>
              {processedRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={data.columns.length + (hasAnyDetail ? 1 : 0)}
                    className="px-3 py-6 text-center text-gray-400 dark:text-gray-500"
                  >
                    Nenhum registro encontrado
                  </td>
                </tr>
              ) : (
                processedRows.map((row, rowIndex) => {
                  const daysOpen = row['dias_aberto'] as number | null
                  const isWarning = daysOpen != null && daysOpen > 7

                  const hasDetail = !!row.detail

                  // Cor por linha vinda do backend (ex: status de Pedido VOR
                  // — change `pedidos-relatorio-csps`). Comportamento:
                  // - Sem `row_color_columns`: pinta a `<tr>` inteira
                  //   (sobrescreve zebrado).
                  // - Com `row_color_columns: ["status"]`: pinta APENAS as
                  //   células dessas colunas — replica como a Maira pinta
                  //   manualmente (só célula de status colorida).
                  const rowColorHex =
                    data.row_color_field &&
                    typeof row[data.row_color_field] === 'string'
                      ? (row[data.row_color_field] as string)
                      : ''
                  const isValidHex =
                    rowColorHex && /^#[0-9a-fA-F]{6}$/.test(rowColorHex)
                  const cellLevelColoring =
                    !!data.row_color_columns &&
                    data.row_color_columns.length > 0
                  const cellColorKeys = new Set(data.row_color_columns ?? [])
                  const rowColorStyle =
                    isValidHex && !cellLevelColoring
                      ? { backgroundColor: rowColorHex }
                      : undefined

                  return (
                    <tr
                      key={rowIndex}
                      onClick={
                        hasDetail ? () => setSelectedRow(row) : undefined
                      }
                      style={rowColorStyle}
                      data-row-color={rowColorHex || undefined}
                      className={`
                        border-b border-gray-100 dark:border-gray-800
                        hover:bg-gold-50 dark:hover:bg-gold-950 transition-colors
                        ${hasDetail ? 'cursor-pointer' : ''}
                        ${
                          rowColorStyle
                            ? ''
                            : rowIndex % 2 === 0
                              ? 'bg-white dark:bg-gray-900'
                              : 'bg-gray-50 dark:bg-gray-800/50'
                        }
                      `}
                    >
                      {/* Botao de detalhe na primeira coluna (mobile-friendly) */}
                      {!hasDetail && hasAnyDetail && <td className="w-8" />}
                      {hasDetail && (
                        <td className="px-1 py-2 text-center w-8">
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              setSelectedRow(row)
                            }}
                            className="p-1 rounded hover:bg-brand-100 dark:hover:bg-brand-900 text-brand-500 transition-colors"
                            title="Ver detalhes"
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                        </td>
                      )}
                      {data.columns.map(col => {
                        const cellValue = row[col.key]
                        const isDaysCol = col.key === 'dias_aberto'
                        const showWarning = isDaysCol && isWarning
                        const statusIcon =
                          col.key === 'detail_status'
                            ? DETAIL_STATUS_ICON[String(cellValue ?? '')]
                            : undefined

                        // Cor por célula (quando row_color_columns define).
                        const cellColorStyle =
                          cellLevelColoring &&
                          isValidHex &&
                          cellColorKeys.has(col.key)
                            ? { backgroundColor: rowColorHex }
                            : undefined
                        const cellWidthStyle = colWidths[col.key]
                          ? { maxWidth: colWidths[col.key] }
                          : { maxWidth: 280 }

                        return (
                          <td
                            key={col.key}
                            style={{ ...cellWidthStyle, ...cellColorStyle }}
                            className={`px-3 py-2 truncate ${
                              col.align === 'right'
                                ? 'text-right'
                                : col.align === 'center'
                                  ? 'text-center'
                                  : 'text-left'
                            } ${showWarning ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-900 dark:text-gray-100'}`}
                            title={statusIcon?.label ?? String(cellValue ?? '')}
                          >
                            {statusIcon ? (
                              <span
                                className={`inline-flex ${statusIcon.color}`}
                              >
                                <Icon
                                  name={statusIcon.name}
                                  size={16}
                                  aria-label={statusIcon.label}
                                />
                              </span>
                            ) : (
                              <>
                                {showWarning && (
                                  <svg
                                    className="w-3 h-3 inline-block mr-0.5 -mt-0.5 text-red-600 dark:text-red-400"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                                {formatCellValue(cellValue, col.format)}
                              </>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Action bar */}
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          {data.exportable && (
            <button
              onClick={handleExportXlsx}
              disabled={exporting}
              title="Exportar Excel"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              {exporting ? (
                <Spinner size="md" className="text-gray-400" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14.5 1H6a2 2 0 00-2 2v18a2 2 0 002 2h12a2 2 0 002-2V6.5L14.5 1z"
                    fill="#185C37"
                  />
                  <path d="M20 6.5H14.5V1L20 6.5z" fill="#21A366" />
                  <path
                    d="M13 12.5H9.5v2H13v-2zm0-3H9.5v2H13v-2zm0 6H9.5v2H13v-2zm3.5-6H13v2h3.5v-2zm0 3H13v2h3.5v-2zm0 3H13v2h3.5v-2z"
                    fill="#fff"
                  />
                </svg>
              )}
            </button>
          )}
          {data.emailable && (
            <button
              onClick={() => setShowEmailModal(true)}
              title="Enviar por Email"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                <rect
                  x="2"
                  y="4"
                  width="20"
                  height="16"
                  rx="2"
                  fill="#EA4335"
                />
                <path d="M22 4L12 13 2 4" fill="#C5221F" />
                <path
                  d="M2 4l10 9 10-9"
                  stroke="#fff"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          )}
          {data.whatsappable && (
            <button
              onClick={() => setShowWhatsAppModal(true)}
              title="Enviar por WhatsApp"
              className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </button>
          )}
          <div className="flex-1" />
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            {processedRows.length !== data.rows.length
              ? `${processedRows.length} de ${data.rows.length}`
              : data.rows.length}{' '}
            registro{data.rows.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Row detail modal */}
      {selectedRow?.detail && (
        <RowDetailModal
          row={selectedRow}
          detail={selectedRow.detail as Record<string, unknown>}
          onClose={() => setSelectedRow(null)}
        />
      )}

      {/* Email modal */}
      {showEmailModal && (
        <EmailReportModal
          data={{ ...data, rows: processedRows }}
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {/* WhatsApp modal — imagem (CSPS) ou texto (HDs e demais) */}
      {showWhatsAppModal &&
        (shouldUseImageWhatsApp(data) ? (
          <WhatsAppMessageModal
            data={{ ...data, rows: processedRows }}
            isOpen={showWhatsAppModal}
            onClose={() => setShowWhatsAppModal(false)}
          />
        ) : (
          <WhatsAppTextModal
            data={{ ...data, rows: processedRows }}
            isOpen={showWhatsAppModal}
            onClose={() => setShowWhatsAppModal(false)}
          />
        ))}
    </>
  )
}

/**
 * Wrapper que detecta `data.secondary_tab` e renderiza uma barra de abas
 * para alternar entre a tabela principal e a secundária. Sem `secondary_tab`,
 * comporta-se exatamente como `<InlineDataTable />`.
 *
 * Cada aba mantém estado próprio (filtros, ordenação, modais de export e
 * WhatsApp) porque cada chamada de `<InlineDataTable />` é uma instância
 * independente. Trocar de aba não preserva filtros — comportamento aceito
 * por enquanto (as duas tabelas têm domínios diferentes).
 */
export const InlineDataTableTabs: React.FC<InlineDataTableProps> = ({
  data,
}) => {
  const [activeTab, setActiveTab] = useState<'primary' | 'secondary'>('primary')

  if (!data.secondary_tab) {
    return <InlineDataTable data={data} />
  }

  const {
    tab_label,
    primary_tab_label,
    table: secondaryTable,
  } = data.secondary_tab
  const activeData = activeTab === 'primary' ? data : secondaryTable

  return (
    <div>
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-2 -mt-1">
        <button
          type="button"
          onClick={() => setActiveTab('primary')}
          className={
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
            (activeTab === 'primary'
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300')
          }
        >
          {primary_tab_label} ({data.rows.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('secondary')}
          className={
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors ' +
            (activeTab === 'secondary'
              ? 'border-primary-500 text-primary-600 dark:text-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300')
          }
        >
          {tab_label} ({secondaryTable.rows.length})
        </button>
      </div>
      <InlineDataTable key={activeTab} data={activeData} />
    </div>
  )
}
